/**
 * Daytona Agent UI sandbox file layout contract.
 *
 * Naming: `*_DIR` constants are workspace-relative (e.g. `dev/website`);
 * `*_PATH` constants are absolute sandbox paths (e.g. `/workspace/dev/website`);
 * `*_SEGMENTS` constants are Drive row paths — the tree names a namespace walk
 * traverses, including structural roots that never appear in a sandbox path.
 *
 * These mirror a layout produced by the snapshot image, so a change here is a
 * multi-artifact migration (pinned by `sandboxPaths.unit.spec.ts`).
 */
function trimPathSlashes(pathSegment: string): string {
  return pathSegment.replace(/^\/+|\/+$/gu, "");
}

// String join only: trims slashes between segments and drops empty segments.
// Does not normalize `..`, reject absolute segments, or dedupe an existing
// workspace prefix — sanitize untrusted segments before calling.
export function joinSandboxPath(...segments: string[]): string {
  const normalizedSegments = segments
    .map((segment, index) => {
      return index === 0
        ? segment.replace(/\/+$/u, "")
        : trimPathSlashes(segment);
    })
    .filter((segment) => segment.length > 0);
  return normalizedSegments.join("/");
}

export const SANDBOX_WORKSPACE_PATH = "/workspace";

// Debug-only sentinel the coordinator writes and the publish tool reads to
// synthesize per-phase failures. Under /run (tmpfs) so it self-clears on restart.
export const SANDBOX_PUBLISH_FAULT_PATH = "/run/replo-publish-fault.json";
// NOTE (Yuxin, 2026-07-26): intentional-local-mode marker; must match
// DISABLED_MARKER in apps/daytona-snapshot/files-volume-probe.sh.
export const SANDBOX_FILES_VOLUME_DISABLED_MARKER_PATH =
  "/run/files-volume-disabled";
// NOTE (Yuxin, 2026-07-26): baked only into the snapshot image, so presence
// means "runs files-volume" — unlike /workspace, which CI containers have too.
export const SANDBOX_FILES_VOLUME_PROBE_SCRIPT_PATH =
  "/etc/s6/files-volume-probe.sh";
export const SANDBOX_DEV_CONTROL_SCRIPT_PATH = "/etc/s6/dev-control.sh";

// Top-level Drive mounts (files-fuse): `files` per-project Drive, `shared` shared-with-me, `project` project-scoped, `user` user-global.
export const SANDBOX_FILES_DIR = "files";
export const SANDBOX_SHARED_DIR = "shared";
export const SANDBOX_PROJECT_DIR = "project";
export const SANDBOX_USER_DIR = "user";

// Machine-canonical context (files-fuse namespace=system): user context is user-global at user/.context; project context is project-scoped at project/.context.
export const SANDBOX_USER_CONTEXT_DIR = joinSandboxPath(
  SANDBOX_USER_DIR,
  ".context",
);
export const SANDBOX_PROJECT_CONTEXT_DIR = joinSandboxPath(
  SANDBOX_PROJECT_DIR,
  ".context",
);

const sandboxAppsDirectory = "apps";
const sandboxBrandStudioDirectory = joinSandboxPath(
  sandboxAppsDirectory,
  "brand-studio",
);
// Project branding is project-scoped context shared by every member's sandbox,
// so it lives under project/.context.
const sandboxProjectBrandingDirectory = joinSandboxPath(
  SANDBOX_PROJECT_CONTEXT_DIR,
  "branding",
);

// Multi-site root: every site is a working tree at `dev/<directory>`. The
// folder name is derived and renameable — canonical identity is the
// `replo.siteId` pin in each tree's package.json, resolved at runtime
// (coordinator `SiteService.getDirectory` / `dev-control.sh dir <siteId>`).
// Build per-site paths with `joinSandboxPath(SANDBOX_DEV_PATH, directory)`.
export const SANDBOX_DEV_DIR = "dev";
export const SANDBOX_BRAND_STUDIO_DRAFTS_DIR = joinSandboxPath(
  sandboxBrandStudioDirectory,
  "drafts",
);
export const SANDBOX_INSIGHTS_DIR = joinSandboxPath(
  sandboxAppsDirectory,
  "insights",
);
export const SANDBOX_OPENCODE_DIR = ".opencode";
export const SANDBOX_OPENCODE_SKILLS_DIR = joinSandboxPath(
  SANDBOX_OPENCODE_DIR,
  "skills",
);
// opencode-distribution-relative paths of the deterministic registry CLIs:
// `scripts/opencode-distribution.ts` esbuild-bundles the `bin/` sources here,
// and coordinator RPCs `exec` the bundled artifacts at these paths.
export const UPLOAD_REGISTRY_ITEMS_CLI_DIST_RELATIVE_PATH =
  "bin/run-upload-registry-items.mjs";
export const INSTALL_REGISTRY_ITEMS_CLI_DIST_RELATIVE_PATH =
  "bin/run-install-registry-items.mjs";
export const PAGE_AUDIT_SCAN_CLI_DIST_RELATIVE_PATH =
  "bin/run-page-audit-scan.mjs";
export const SANDBOX_SKILL_FILENAME = "SKILL.md";
// NOTE (Ezra, 2026-07-01, REPL-28638): machine-image root the snapshot COPYs
// the harness into (apps/daytona-snapshot Dockerfile). Distinct from
// SANDBOX_OPENCODE_PATH (`/workspace/.opencode`, the workspace skills mount) —
// the bundled CLI lives here, so the two are easy to confuse.
export const SANDBOX_MACHINE_OPENCODE_CONFIG_PATH = "/root/.config/opencode";
// NOTE (Andrew, 2026-07-16): opencode's sandbox-local data dir — opencode.db (session SQLite) lives here, not under /workspace.
export const SANDBOX_OPENCODE_DATA_PATH = "/root/.local/share/opencode";
// Absolute machine-image paths owned by opencode + the snapshot image
// (apps/daytona-snapshot Dockerfile), not the workspace layout.
export const SANDBOX_BUILTIN_SKILL_PATHS = [
  "/root/.opencode/skills",
  joinSandboxPath(SANDBOX_MACHINE_OPENCODE_CONFIG_PATH, "skills"),
] as const;
// Absolute paths the coordinator registry RPCs exec. Joined here (not in the
// harness build) so the coordinator stays off the esbuild graph.
export const SANDBOX_UPLOAD_REGISTRY_ITEMS_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  UPLOAD_REGISTRY_ITEMS_CLI_DIST_RELATIVE_PATH,
);
export const SANDBOX_INSTALL_REGISTRY_ITEMS_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  INSTALL_REGISTRY_ITEMS_CLI_DIST_RELATIVE_PATH,
);
export const SANDBOX_PAGE_AUDIT_SCAN_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  PAGE_AUDIT_SCAN_CLI_DIST_RELATIVE_PATH,
);
export const SANDBOX_PLANS_DIR = ".plans";
export const SANDBOX_BRANDING_DIR = sandboxProjectBrandingDirectory;
export const SANDBOX_DEFAULT_BRAND_DIR = joinSandboxPath(
  SANDBOX_BRANDING_DIR,
  "default",
);

// Namespace-relative Drive paths for durable dirs whose Drive folder name differs
// from the dotted workspace alias above. `files-changed` event paths are built by
// bedrock-api's `resolvePath`, which walks Drive folder *names*, so consumer globs
// and the coordinator's skill-restart check must match on THESE, not the dotted
// SANDBOX_*_DIR aliases.
//
// The Drive deliberately names these without a leading dot so they show in the
// Files app; the sandbox re-exposes them at the dotted paths via files-volume.sh
// symlinks (PERSISTED_DIRS: `.plans:plans`, `.opencode/skills:skills`). Project
// branding lives under the project's team context, and `resolvePath` strips only
// the structural `shared/` root — so its event paths keep the `.context/` prefix.
export const DRIVE_PLANS_DIR = "plans";
export const DRIVE_SKILLS_DIR = "skills";
export const DRIVE_DEFAULT_BRAND_DIR = ".context/branding/default";

// NOTE (Noah, 2026-08-11): row segments, NOT an event path — the two coordinate
// systems here differ by exactly the structural `shared/` root, which is
// invisible everywhere else. A Drive row walk starts at the true root and so
// must carry it; `resolvePath` strips it before any event or client-side path,
// which is why `DRIVE_DEFAULT_BRAND_DIR` above lacks it. Build `files-changed`
// globs from that one; address Drive rows with this one.
export const DRIVE_DEFAULT_BRAND_BUSINESS_MD_SEGMENTS = [
  SANDBOX_SHARED_DIR,
  ...DRIVE_DEFAULT_BRAND_DIR.split("/"),
  "BUSINESS.md",
];

export const DRIVE_DEFAULT_BRAND_PROJECT_CONTEXT_MD_SEGMENTS = [
  SANDBOX_SHARED_DIR,
  ...DRIVE_DEFAULT_BRAND_DIR.split("/"),
  "project-context.md",
];

export const DRIVE_DEFAULT_BRAND_DESIGN_MD_SEGMENTS = [
  SANDBOX_SHARED_DIR,
  ...DRIVE_DEFAULT_BRAND_DIR.split("/"),
  "DESIGN.md",
];

export const SANDBOX_DEV_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_DEV_DIR,
);
export const SANDBOX_BRAND_STUDIO_DRAFTS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_BRAND_STUDIO_DRAFTS_DIR,
);
export const SANDBOX_INSIGHTS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_INSIGHTS_DIR,
);
export const SANDBOX_OPENCODE_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_OPENCODE_DIR,
);
export const SANDBOX_OPENCODE_SKILLS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_OPENCODE_SKILLS_DIR,
);
export const SANDBOX_PLANS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PLANS_DIR,
);
export const SANDBOX_FILES_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_FILES_DIR,
);
export const SANDBOX_SHARED_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_SHARED_DIR,
);
export const SANDBOX_PROJECT_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PROJECT_DIR,
);
export const SANDBOX_USER_CONTEXT_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_USER_CONTEXT_DIR,
);
export const SANDBOX_PROJECT_CONTEXT_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PROJECT_CONTEXT_DIR,
);
export const SANDBOX_BRANDING_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_BRANDING_DIR,
);
export const SANDBOX_DEFAULT_BRAND_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_DEFAULT_BRAND_DIR,
);
export const BUSINESS_MD_PATH = joinSandboxPath(
  SANDBOX_DEFAULT_BRAND_PATH,
  "BUSINESS.md",
);
export const PROJECT_CONTEXT_MD_PATH = joinSandboxPath(
  SANDBOX_DEFAULT_BRAND_PATH,
  "project-context.md",
);
export const DESIGN_MD_PATH = joinSandboxPath(
  SANDBOX_DEFAULT_BRAND_PATH,
  "DESIGN.md",
);
// NOTE (Ezra, 2026-06-30, REPL-28543): per-site key for a shared page — the same
// route in sibling sites of one project must not collide on one registry row.
// The editor (record lookup) and the upload tool (write) both build it here so
// the keys can't drift; `siteDirectory` is one dir segment (e.g. `website-2`).
export function registryPageRouteSourceKey({
  siteDirectory,
  route,
}: {
  siteDirectory: string;
  route: string;
}): string {
  // `|| "/"` (not `??`): a bare "/" route strips to "" and must fall back to "/".
  const normalizedRoute = route.replace(/\/+$/u, "") || "/";
  return `${siteDirectory}:${normalizedRoute}`;
}

export function getSandboxWorkspaceRelativePath(path: string): string | null {
  if (path === SANDBOX_WORKSPACE_PATH) {
    return "";
  }
  const workspacePrefix = `${SANDBOX_WORKSPACE_PATH}/`;
  return path.startsWith(workspacePrefix)
    ? path.slice(workspacePrefix.length)
    : null;
}
